/* TYPE TEST // WIRED IN — vanilla port of the Framer TypeTest.tsx code component.
 * Source of truth for behaviour: _scratch/type-test-port/TypeTest.framer-source.tsx
 *
 * Ported 2026-07-22. No React, no dependencies. Faithful to the original:
 *   modes time(15/30/60) + words(25/50/100), 5 font options, WPM / Raw / Accuracy /
 *   Errors / Consistency, Enter=start, Ctrl+Enter=restart, Ctrl+Backspace=clear word,
 *   localStorage PB, animated count-up, gliding caret, // MISS MAP heatmap,
 *   downloadable branded result card.
 *
 * Deliberate deviations from the Framer original (documented, not accidental):
 *   1. Fonts come from the theme's own snippets/win-fonts.liquid families
 *      ("PP Fraktion Mono WIN" / "PP Fraktion Sans WIN") instead of the
 *      framerusercontent.com woff2 URLs — kills the last Framer dependency.
 *   2. Whole-page dark mode flips the theme's Dawn colour-scheme RGB triplets
 *      instead of hunting Framer-generated token/preset hashes. The original's own
 *      comment flagged that approach as needing re-sampling whenever the page was
 *      edited; this one has no such fragility.
 *   3. The word list re-renders only the words that actually changed, rather than
 *      the whole list every keystroke (React did the latter).
 */
(function () {
  "use strict";

  var MONO = "'PP Fraktion Mono WIN', ui-monospace, 'SFMono-Regular', Menlo, monospace";
  var SANS = "'PP Fraktion Sans WIN', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif";
  var WIN_FEATURES = '"cv03" on, "cv04" on, "cv09" on, "cv11" on';

  var FONT_OPTIONS = [
    { label: "MONO · REG", family: MONO, weight: 400 },
    { label: "MONO · BOLD", family: MONO, weight: 700 },
    { label: "SANS · THIN", family: SANS, weight: 200 },
    { label: "SANS · REG", family: SANS, weight: 400 },
    { label: "SANS · BOLD", family: SANS, weight: 700 }
  ];

  var WORD_POOL = ("the of and to in a is that it was for on are as with his they at be this from or had by hot but some what there we can out other were all your when up use word how said an each she which do their time if will way about many then them would write like so these her long make thing see him two has look more day could go come did my sound no most number who over know water than call first people may down side been now find any new work part take get place made live where after back little only round man year came show every good me give our under name very through just form much great think say help low line before turn cause same mean differ move right boy old too does tell set three want air well also play small end put home read hand port large spell add even land here must big high such follow act why ask men change went light kind off need house picture try us again animal point mother world near build self earth father head stand own page should country found answer school grow study still learn plant cover food sun four between state keep eye never last let thought city tree cross farm hard start might story saw far sea draw left late run dont while press close night real life few stop open seem together next white children begin got walk example ease paper group always music those both mark often letter until mile river car feet care second book carry took science eat room friend began idea fish mountain north once base hear horse cut sure watch color face wood main enough plain girl usual young ready above ever red list though feel talk bird soon body dog family direct pose leave song measure door product black short numeral class wind question happen complete ship area half rock order fire south problem piece told knew pass since top whole king space heard best hour better true during hundred am remember step early hold west ground interest reach fast verb sing listen six table travel less morning ten simple several vowel toward war lay against pattern slow center love person money serve appear road map rain rule govern pull cold notice voice unit power town fine certain fly fall lead cry dark machine note wait plan figure star box noun field rest correct able pound done beauty drive stood contain front teach week final gave green oh quick develop ocean warm free minute strong special mind behind clear tail produce fact street inch multiply nothing course stay wheel full force blue object decide surface deep moon island foot system busy test record boat common gold possible plane stead dry wonder laugh thousand ago ran check game shape equate miss brought heat snow tire bring yes distant fill east paint language among").split(" ");

  var TIME_OPTS = [15, 30, 60];
  var WORD_OPTS = [25, 50, 100];
  var KB_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

  function shuffleTake(pool, n, exclude) {
    var out = [];
    var prev = exclude && exclude.length ? exclude[exclude.length - 1] : "";
    for (var i = 0; i < n; i++) {
      var w = pool[Math.floor(Math.random() * pool.length)];
      var guard = 0;
      while (w === prev && guard < 5) {
        w = pool[Math.floor(Math.random() * pool.length)];
        guard++;
      }
      out.push(w);
      prev = w;
    }
    return out;
  }

  // Build an rgba() from hex or rgb()/rgba() + alpha. Never string-concat a hex
  // alpha suffix — a `${c}40` on an rgb() string is invalid and silently dropped.
  function withAlpha(color, a) {
    if (!color) return color;
    var s = String(color).trim();
    if (s.indexOf("rgb") === 0) {
      var n = s.replace(/rgba?\(|\)/g, "").split(",").map(function (x) { return x.trim(); });
      return "rgba(" + n[0] + ", " + n[1] + ", " + n[2] + ", " + a + ")";
    }
    var h = s.replace("#", "");
    if (h.length === 3) h = h.split("").map(function (x) { return x + x; }).join("");
    return "rgba(" + parseInt(h.slice(0, 2), 16) + ", " + parseInt(h.slice(2, 4), 16) + ", " + parseInt(h.slice(4, 6), 16) + ", " + a + ")";
  }

  function rgbTriplet(color) {
    var m = withAlpha(color, 1).match(/rgba?\(([^)]+)\)/);
    if (!m) return "18,18,18";
    var p = m[1].split(",").map(function (x) { return x.trim(); });
    return p[0] + "," + p[1] + "," + p[2];
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* ---- branded result card → PNG (Canvas 2D, no dependencies) ---- */
  function drawResultCard(result, samples, c) {
    var W = 1080, H = 1080, P = 84;
    var canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    var ctx = canvas.getContext("2d");
    if (!ctx) return Promise.resolve("");

    var ready = document.fonts
      ? Promise.all([
          document.fonts.load('500 300px "PP Fraktion Sans WIN"'),
          document.fonts.load('500 60px "PP Fraktion Sans WIN"'),
          document.fonts.load('400 24px "PP Fraktion Mono WIN"')
        ]).catch(function () {})
      : Promise.resolve();

    return ready.then(function () {
      var MONO_C = '"PP Fraktion Mono WIN"';
      var SANS_C = '"PP Fraktion Sans WIN"';

      ctx.fillStyle = c.dark;
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(239,238,232,0.25)";
      ctx.lineWidth = 2;
      var bb = 26, of = 46;
      function bracket(x, y, dx, dy) {
        ctx.beginPath();
        ctx.moveTo(x + dx * bb, y);
        ctx.lineTo(x, y);
        ctx.lineTo(x, y + dy * bb);
        ctx.stroke();
      }
      bracket(of, of, 1, 1);
      bracket(W - of, of, -1, 1);
      bracket(of, H - of, 1, -1);
      bracket(W - of, H - of, -1, -1);

      ctx.fillStyle = "rgba(239,238,232,0.5)";
      ctx.font = "400 24px " + MONO_C;
      ctx.fillText("// WIRED IN", P, P + 28);
      ctx.fillStyle = c.accent;
      ctx.fillRect(P, P + 48, 92, 10);
      ctx.fillStyle = c.cream;
      ctx.font = "500 66px " + SANS_C;
      ctx.fillText("TYPE TEST", P, P + 152);

      ctx.textAlign = "right";
      ctx.fillStyle = "rgba(239,238,232,0.5)";
      ctx.font = "400 24px " + MONO_C;
      ctx.fillText("SPEED BENCH", W - P, P + 28);
      ctx.fillText(String(result.setting || "").toUpperCase() + " · " + String(result.mode || "").toUpperCase(), W - P, P + 60);
      ctx.textAlign = "left";

      var wpmStr = String(result.wpm);
      var wpmSize = wpmStr.length >= 4 ? 200 : 300;
      ctx.fillStyle = c.accent;
      ctx.font = "500 " + wpmSize + "px " + SANS_C;
      ctx.fillText(wpmStr, P - 4, 560);
      var wpmW = ctx.measureText(wpmStr).width;
      ctx.fillStyle = "rgba(239,238,232,0.5)";
      ctx.font = "400 26px " + MONO_C;
      ctx.fillText("WPM", P + wpmW + 26, 560);

      if (result.isRecord) {
        ctx.fillStyle = c.accent;
        ctx.font = "400 24px " + MONO_C;
        ctx.fillText("★ NEW RECORD", P + wpmW + 26, 512);
      } else if (result.pb) {
        ctx.fillStyle = "rgba(239,238,232,0.4)";
        ctx.font = "400 24px " + MONO_C;
        ctx.fillText("PB " + result.pb, P + wpmW + 26, 512);
      }

      var stats = [
        ["RAW", result.raw],
        ["ACCURACY", result.acc + "%"],
        ["ERRORS", result.errors],
        ["CONSISTENCY", result.consistency + "%"]
      ];
      var sx = P, sy = 700;
      stats.forEach(function (s) {
        ctx.fillStyle = "rgba(239,238,232,0.5)";
        ctx.font = "400 22px " + MONO_C;
        ctx.fillText(String(s[0]), sx, sy);
        ctx.fillStyle = c.cream;
        ctx.font = "500 58px " + SANS_C;
        ctx.fillText(String(s[1]), sx, sy + 60);
        sx += 232;
      });

      if (samples && samples.length > 1) {
        var gx = P, gy = 824, gw = W - 2 * P, gh = 92;
        var maxS = Math.max.apply(null, [1].concat(samples));
        var bw = gw / samples.length;
        ctx.fillStyle = "rgba(239,238,232,0.5)";
        ctx.font = "400 20px " + MONO_C;
        ctx.fillText("WPM / SECOND", gx, gy - 14);
        ctx.fillStyle = "rgba(239,238,232,0.15)";
        ctx.fillRect(gx, gy + gh, gw, 1);
        samples.forEach(function (s, i) {
          var h = Math.max(3, (s / maxS) * gh);
          ctx.globalAlpha = 0.4 + 0.6 * (s / maxS);
          ctx.fillStyle = c.accent;
          ctx.fillRect(gx + i * bw, gy + gh - h, Math.max(2, bw - 3), h);
        });
        ctx.globalAlpha = 1;
      }

      ctx.fillStyle = "rgba(239,238,232,0.4)";
      ctx.font = "400 22px " + MONO_C;
      ctx.fillText("TT.001 · WIRED IN //", P, H - P + 8);
      ctx.textAlign = "right";
      ctx.fillText("wiredinstore.com", W - P, H - P + 8);
      ctx.textAlign = "left";

      return canvas.toDataURL("image/png");
    });
  }

  /* ============================ instance ============================ */
  function initTypeTest(root) {
    if (root.dataset.wiTtReady === "1") return;
    root.dataset.wiTtReady = "1";

    var cfg = {};
    try { cfg = JSON.parse(root.dataset.wiTtConfig || "{}"); } catch (e) {}

    var accent = cfg.accent || "#FF4D00";
    var inkProp = cfg.ink || "#151515";
    var mutedProp = cfg.muted || "#A6A49C";
    var hairlineProp = cfg.hairline || "rgba(21,21,21,0.14)";
    var darkCol = cfg.dark || "#121212";
    var creamCol = cfg.cream || "#EFEEE8";
    var discordUrl = cfg.discordUrl || "https://discord.gg/wiredin";
    var pageFlip = cfg.pageFlip !== false;
    var fillViewport = cfg.fillViewport !== false;

    // ---- state ----
    var mode = cfg.defaultMode === "words" ? "words" : "time";
    var timeOpt = TIME_OPTS.indexOf(cfg.defaultTime) >= 0 ? cfg.defaultTime : 30;
    var wordOpt = WORD_OPTS.indexOf(cfg.defaultWords) >= 0 ? cfg.defaultWords : 50;
    var fontIdx = cfg.defaultFont >= 0 && cfg.defaultFont < FONT_OPTIONS.length ? cfg.defaultFont : 0;
    var themeMode = cfg.defaultTheme === "dark" ? "dark" : "light";

    var words = [], typed = [], active = 0, prevActive = 0;
    var status = "idle", focused = false;
    var elapsed = 0, liveWpm = 0, samples = [], result = null;

    var startAt = 0, correct = 0, incorrect = 0, lastRaw = 0;
    var errKey = {}, pb = 0;
    var tickTimer = null, samplerTimer = null;

    try {
      var v = parseInt(localStorage.getItem("wi_tt_pb") || "0", 10);
      if (v > 0) pb = v;
      var t = localStorage.getItem("wi_tt_theme");
      if (t === "light" || t === "dark") themeMode = t;
    } catch (e) {}

    // ---- palette ----
    var pal = {};
    function computePalette() {
      var isDark = themeMode === "dark";
      pal = isDark
        ? {
            isDark: true,
            bg: darkCol,
            ink: creamCol,
            sub: withAlpha(creamCol, 0.5),
            dim: withAlpha(creamCol, 0.42),
            line: withAlpha(creamCol, 0.16),
            surface: withAlpha(creamCol, 0.1)
          }
        : {
            isDark: false,
            bg: "transparent",
            ink: inkProp,
            sub: mutedProp,
            dim: mutedProp,
            line: hairlineProp,
            surface: creamCol
          };
      root.style.setProperty("--tt-accent", accent);
      root.style.setProperty("--tt-ink", pal.ink);
      root.style.setProperty("--tt-muted", pal.sub);
      root.style.setProperty("--tt-dim", pal.dim);
      root.style.setProperty("--tt-line", pal.line);
      root.style.setProperty("--tt-surface", pal.surface);
      root.style.setProperty("--tt-bg", pal.bg);
      root.style.setProperty("--tt-dark", darkCol);
      root.style.setProperty("--tt-cream", creamCol);
      root.style.setProperty("--tt-mono", MONO);
      root.style.setProperty("--tt-sans", SANS);
      root.setAttribute("data-tt-theme", isDark ? "dark" : "light");
    }

    /* ---- WHOLE-PAGE dark mode ----------------------------------------------
     * The Framer original reached into Framer-generated token/preset hashes to
     * re-skin nav + footer + page bg, and its own comment warned those hashes
     * need re-sampling whenever the page is edited. The theme exposes a proper
     * Dawn colour-scheme system, so we flip the RGB triplets instead — stable
     * across theme edits, and it covers header/footer/background in one go.
     */
    var PAGE_STYLE_ID = "wi-tt-page-theme";
    function applyPageTheme() {
      if (!pageFlip) return;
      var darkRgb = rgbTriplet(darkCol);
      var creamRgb = rgbTriplet(creamCol);
      var css =
        'html[data-wi-theme="dark"] body{background:' + darkCol + " !important;color:" + creamCol + " !important;}\n" +
        'html[data-wi-theme="dark"] .color-background-1,' +
        'html[data-wi-theme="dark"] .color-background-2,' +
        'html[data-wi-theme="dark"] .color-scheme-1,' +
        'html[data-wi-theme="dark"] .color-inverse,' +
        'html[data-wi-theme="dark"] .shopify-section{' +
        "--color-background:" + darkRgb + " !important;" +
        "--color-foreground:" + creamRgb + " !important;" +
        "--color-base-background-1:" + darkRgb + " !important;" +
        "--color-base-text:" + creamRgb + " !important;}\n" +
        // Dawn's .gradient class paints from --gradient-background, which is a
        // LITERAL colour (e.g. #F2F1EE), not rgb(var(--color-background)) — so
        // overriding the RGB triplets alone leaves the header/footer wrappers light.
        // The header logo is cream-filled, so a light wrapper renders it invisible.
        'html[data-wi-theme="dark"] .gradient,' +
        'html[data-wi-theme="dark"] .header-wrapper,' +
        'html[data-wi-theme="dark"] .footer-wrapper,' +
        'html[data-wi-theme="dark"] .shopify-section-group-header-group,' +
        'html[data-wi-theme="dark"] .shopify-section-group-footer-group{' +
        "--gradient-background:" + darkCol + " !important;" +
        "background-color:" + darkCol + " !important;" +
        "background-image:none !important;}\n" +
        'html[data-wi-theme="dark"] .header,' +
        'html[data-wi-theme="dark"] .footer{background:' + darkCol + " !important;}\n" +
        'html[data-wi-theme="dark"] .header a,' +
        'html[data-wi-theme="dark"] .footer a,' +
        'html[data-wi-theme="dark"] .header__menu-item,' +
        'html[data-wi-theme="dark"] .footer__content-bottom{color:' + creamCol + " !important;}\n" +
        'html[data-wi-theme="dark"] .header svg,' +
        'html[data-wi-theme="dark"] .footer svg{fill:' + creamCol + ";color:" + creamCol + " !important;}\n" +
        // The wordmark/icon is an <img> (flat single-tone #151515 mark), so fill/color
        // can't reach it. invert() lands it on ~#EAEAEA — essentially brand cream.
        // Verified flat: 743k/749k opaque pixels are one tone, so there are no
        // alpha-distinct layers to collapse (cf. the MonacoKeys brightness(0) incident).
        'html[data-wi-theme="dark"] .header__heading-logo,' +
        'html[data-wi-theme="dark"] .footer__logo{filter:invert(1) !important;}\n';

      var tag = document.getElementById(PAGE_STYLE_ID);
      if (!tag) {
        tag = document.createElement("style");
        tag.id = PAGE_STYLE_ID;
        document.head.appendChild(tag);
      }
      tag.textContent = css;
      document.documentElement.setAttribute("data-wi-theme", themeMode);
      if (themeMode === "dark") {
        document.body.style.setProperty("background", darkCol, "important");
      } else {
        document.body.style.removeProperty("background");
      }
    }

    function setTheme(next) {
      themeMode = next;
      try { localStorage.setItem("wi_tt_theme", themeMode); } catch (e) {}
      computePalette();
      applyPageTheme();
      syncToggle();
      if (result) renderResults();
    }

    /* ---------------------------- DOM shell ---------------------------- */
    var mount = root.querySelector("[data-wi-tt-mount]") || root;
    mount.innerHTML = "";

    // hidden input — kept mounted in every state so Enter / Ctrl+Enter work on
    // the results screen too
    var input = document.createElement("input");
    input.className = "wi-tt__input";
    input.setAttribute("autocomplete", "off");
    input.setAttribute("autocorrect", "off");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("spellcheck", "false");
    input.setAttribute("aria-label", "Typing test input");
    mount.appendChild(input);

    // ---- control bar ----
    var bar = el("div", "wi-tt__bar");
    mount.appendChild(bar);

    function chip(label, on, onClick) {
      var s = el("span", "wi-tt__chip" + (on ? " is-on" : ""), label);
      s.setAttribute("role", "button");
      s.tabIndex = -1;
      s.addEventListener("mousedown", function (e) {
        e.preventDefault();
        onClick();
      });
      return s;
    }

    function group(labelText) {
      var g = el("div", "wi-tt__group");
      g.appendChild(el("span", "wi-tt__label", labelText));
      return g;
    }

    var modeGroup, countGroup, fontGroup, toggleWrap;

    function renderBar() {
      bar.innerHTML = "";

      modeGroup = group("MODE");
      modeGroup.appendChild(chip("Time", mode === "time", function () { setMode("time"); }));
      modeGroup.appendChild(chip("Words", mode === "words", function () { setMode("words"); }));
      bar.appendChild(modeGroup);

      countGroup = group(mode === "time" ? "SECONDS" : "WORDS");
      (mode === "time" ? TIME_OPTS : WORD_OPTS).forEach(function (v) {
        countGroup.appendChild(
          chip(String(v), (mode === "time" ? timeOpt : wordOpt) === v, function () {
            if (mode === "time") timeOpt = v; else wordOpt = v;
            renderBar();
            resetTest(true);
          })
        );
      });
      bar.appendChild(countGroup);

      fontGroup = group("FONT");
      FONT_OPTIONS.forEach(function (f, i) {
        fontGroup.appendChild(chip(f.label, fontIdx === i, function () {
          fontIdx = i;
          renderBar();
          applyTypingFont();
          measureCaret();
        }));
      });
      bar.appendChild(fontGroup);

      var restart = el("span", "wi-tt__restart");
      restart.setAttribute("role", "button");
      restart.tabIndex = -1;
      var glyph = el("span", "wi-tt__restart-glyph", "↻");
      restart.appendChild(glyph);
      restart.appendChild(document.createTextNode(" RESTART"));
      restart.addEventListener("mousedown", function (e) {
        e.preventDefault();
        resetTest(true);
      });
      bar.appendChild(restart);
    }

    // ---- theme toggle (Dieter Rams physical switch, ported 1:1) ----
    var toggle = el("span", "wi-tt__toggle");
    toggle.setAttribute("role", "switch");
    toggle.setAttribute("aria-label", "Toggle dark mode");
    toggle.tabIndex = -1;
    toggle.innerHTML =
      '<span class="wi-tt__toggle-slot wi-tt__toggle-slot--l">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
      '<circle cx="12" cy="12" r="4"/><line x1="12" y1="1.5" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22.5"/>' +
      '<line x1="1.5" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22.5" y2="12"/><line x1="4.4" y1="4.4" x2="6.1" y2="6.1"/>' +
      '<line x1="17.9" y1="17.9" x2="19.6" y2="19.6"/><line x1="4.4" y1="19.6" x2="6.1" y2="17.9"/><line x1="17.9" y1="6.1" x2="19.6" y2="4.4"/></svg></span>' +
      '<span class="wi-tt__toggle-slot wi-tt__toggle-slot--r">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">' +
      '<path d="M21 12.8A8.5 8.5 0 1 1 11.2 3 6.6 6.6 0 0 0 21 12.8Z"/></svg></span>' +
      '<span class="wi-tt__toggle-knob" aria-hidden="true"></span>';
    toggle.addEventListener("mousedown", function (e) {
      e.preventDefault();
      setTheme(themeMode === "dark" ? "light" : "dark");
    });
    function syncToggle() {
      toggle.setAttribute("aria-checked", themeMode === "dark" ? "true" : "false");
    }
    var toggleHost = root.querySelector("[data-wi-tt-toggle]");
    if (toggleHost) toggleHost.appendChild(toggle);

    // ---- live readout ----
    var readout = el("div", "wi-tt__readout");
    var rTimeVal = el("span", "wi-tt__big");
    var rTimeLab = el("span", "wi-tt__label");
    var rWpmVal = el("span", "wi-tt__big wi-tt__big--ink");
    var rWpmLab = el("span", "wi-tt__label", "wpm");
    var rProgWrap = el("div", "wi-tt__stat");
    var rProgVal = el("span", "wi-tt__big wi-tt__big--ink");

    (function buildReadout() {
      var a = el("div", "wi-tt__stat");
      a.appendChild(rTimeVal);
      a.appendChild(rTimeLab);
      readout.appendChild(a);
      readout.appendChild(el("div", "wi-tt__rule"));
      var b = el("div", "wi-tt__stat");
      b.appendChild(rWpmVal);
      b.appendChild(rWpmLab);
      readout.appendChild(b);
      rProgWrap.appendChild(rProgVal);
    })();
    mount.appendChild(readout);

    // ---- typing surface ----
    var surface = el("div", "wi-tt__surface");
    ["tl", "tr", "bl", "br"].forEach(function (p) {
      surface.appendChild(el("span", "wi-tt__corner wi-tt__corner--" + p));
    });
    var viewport = el("div", "wi-tt__viewport");
    var inner = el("div", "wi-tt__inner");
    var caret = el("span", "wi-tt__caret");
    inner.appendChild(caret);
    viewport.appendChild(inner);
    surface.appendChild(viewport);

    var hint = el("div", "wi-tt__hint");
    var hintBadge = el("span", "wi-tt__hint-badge", "click or press enter to begin");
    hint.appendChild(hintBadge);
    surface.appendChild(hint);

    surface.addEventListener("mousedown", function (e) {
      // preventDefault stops the browser's default focus-change from blurring
      // our hidden input right back
      e.preventDefault();
      input.focus();
    });
    mount.appendChild(surface);

    // ---- results host ----
    var resultsHost = el("div", "wi-tt__results-host");
    mount.appendChild(resultsHost);

    // ---- footer hints ----
    var foot = el("div", "wi-tt__foot");
    foot.innerHTML =
      '<span><b>ENTER</b> ↵ START</span>' +
      '<span><b>CTRL + ENTER</b> RESTART</span>' +
      '<span><b>CTRL + ⌫</b> CLEAR WORD</span>' +
      '<span class="wi-tt__foot-id">TT.001 · WIRED IN //</span>';
    mount.appendChild(foot);

    /* ---------------------------- rendering ---------------------------- */
    var wordEls = [];

    function applyTypingFont() {
      var f = FONT_OPTIONS[fontIdx] || FONT_OPTIONS[0];
      viewport.style.fontFamily = f.family;
      viewport.style.fontWeight = String(f.weight);
      viewport.style.letterSpacing = f.family === MONO ? "0px" : "0.2px";
    }

    function buildWordEl(wi) {
      var w = words[wi] || "";
      var t = typed[wi] || "";
      var isActive = wi === active;
      var isPast = wi < active;
      var span = el("span", "wi-tt__word");
      var len = Math.max(w.length, t.length);
      for (var i = 0; i < len; i++) {
        var tch = t[i];
        var wch = w[i];
        var cls = "wi-tt__ch";
        var extra = false, underline = false, tone = "dim";
        if (i < t.length) {
          if (i < w.length) {
            tone = tch === wch ? "ink" : "accent";
            underline = tch !== wch;
          } else {
            tone = "accent";
            extra = true;
            underline = true;
          }
        } else if (isPast && i < w.length) {
          underline = true;
          tone = "dim";
        }
        if (isActive && i === t.length) {
          span.appendChild(el("span", "wi-tt__anchor"));
        }
        var ch = el("span", cls + " is-" + tone + (underline ? " is-miss" : "") + (extra ? " is-extra" : ""));
        ch.textContent = wch != null ? wch : tch;
        span.appendChild(ch);
      }
      if (isActive && t.length >= len) {
        span.appendChild(el("span", "wi-tt__anchor"));
      }
      return span;
    }

    function renderAllWords() {
      inner.innerHTML = "";
      inner.appendChild(caret);
      wordEls = [];
      for (var i = 0; i < words.length; i++) {
        var n = buildWordEl(i);
        wordEls.push(n);
        inner.appendChild(n);
      }
      scrollToActive();
      measureCaret();
    }

    // Only the words that actually changed get rebuilt (React rebuilt all of them
    // on every keystroke; this keeps long time-mode runs cheap).
    function refreshWord(i) {
      if (i < 0 || i >= words.length || !wordEls[i]) return;
      var fresh = buildWordEl(i);
      inner.replaceChild(fresh, wordEls[i]);
      wordEls[i] = fresh;
    }

    function repaintActive() {
      if (prevActive !== active) refreshWord(prevActive);
      refreshWord(active);
      prevActive = active;
      scrollToActive();
      measureCaret();
    }

    function scrollToActive() {
      var elw = wordEls[active];
      if (!elw) return;
      var top = elw.offsetTop;
      var lh = elw.offsetHeight || 0;
      inner.style.transform = "translateY(" + -Math.max(0, top - lh) + "px)";
    }

    function measureCaret() {
      var elw = wordEls[active];
      if (!elw) return;
      var a = elw.querySelector(".wi-tt__anchor");
      if (!a) return;
      var ar = a.getBoundingClientRect();
      var ir = inner.getBoundingClientRect();
      var h = ar.height;
      caret.style.left = ar.left - ir.left + "px";
      caret.style.top = ar.top - ir.top + (h ? h * 0.13 : 0) + "px";
      caret.style.height = (h ? h * 0.74 : 18) + "px";
    }

    function updateReadout() {
      var showing = !!result;
      readout.classList.toggle("is-hidden", showing);
      if (mode === "time") {
        rTimeVal.textContent = String(Math.max(0, Math.ceil(timeOpt - elapsed)));
        rTimeLab.textContent = "sec left";
      } else {
        rTimeVal.textContent = String(Math.round(elapsed));
        rTimeLab.textContent = "sec";
      }
      rWpmVal.textContent = status === "running" ? String(liveWpm) : "—";
      if (mode === "words") {
        if (!rProgWrap.parentNode) {
          readout.appendChild(el("div", "wi-tt__rule"));
          readout.appendChild(rProgWrap);
        }
        rProgVal.textContent = Math.min(active, words.length) + " / " + words.length;
      } else if (rProgWrap.parentNode) {
        readout.removeChild(rProgWrap.previousSibling);
        readout.removeChild(rProgWrap);
      }
    }

    function updateFocusState() {
      var blurred = !focused && status !== "running";
      surface.classList.toggle("is-blurred", blurred);
      hint.style.display = blurred ? "flex" : "none";
      hintBadge.textContent = status === "idle" ? "click or press enter to begin" : "click to resume";
    }

    /* ---------------------------- test flow ---------------------------- */
    function newWords(count) {
      words = shuffleTake(WORD_POOL, count);
      typed = words.map(function () { return ""; });
    }

    function setMode(next) {
      if (mode === next) return;
      mode = next;
      renderBar();
      resetTest(true);
    }

    function resetTest(regen) {
      correct = 0;
      incorrect = 0;
      lastRaw = 0;
      startAt = 0;
      errKey = {};
      active = 0;
      prevActive = 0;
      status = "idle";
      elapsed = 0;
      liveWpm = 0;
      samples = [];
      result = null;
      stopTimers();
      resultsHost.innerHTML = "";
      resultsHost.classList.remove("is-on");
      surface.style.display = "";
      if (regen !== false) {
        newWords(mode === "words" ? wordOpt : 90);
      } else {
        typed = words.map(function () { return ""; });
      }
      renderAllWords();
      updateReadout();
      updateFocusState();
      input.focus();
    }

    function startIfIdle() {
      if (status !== "idle") return;
      status = "running";
      startAt = Date.now();
      lastRaw = 0;
      updateFocusState();
      startTimers();
    }

    function startTimers() {
      stopTimers();
      tickTimer = window.setInterval(function () {
        var e = (Date.now() - startAt) / 1000;
        elapsed = e;
        liveWpm = e > 0 ? Math.round(correct / 5 / (e / 60)) : 0;
        updateReadout();
        if (mode === "time" && e >= timeOpt) finishTest(timeOpt);
      }, 100);
      samplerTimer = window.setInterval(function () {
        var raw = correct + incorrect;
        var delta = raw - lastRaw;
        lastRaw = raw;
        samples.push(Math.round((delta / 5) * 60));
      }, 1000);
    }

    function stopTimers() {
      if (tickTimer) window.clearInterval(tickTimer);
      if (samplerTimer) window.clearInterval(samplerTimer);
      tickTimer = samplerTimer = null;
    }

    function finishTest(forcedSeconds) {
      if (status === "finished") return;
      status = "finished";
      stopTimers();
      var secs = forcedSeconds != null ? forcedSeconds : Math.max(0.001, (Date.now() - startAt) / 1000);
      var minutes = secs / 60;
      var wpm = Math.round(correct / 5 / minutes);
      var raw = Math.round((correct + incorrect) / 5 / minutes);
      var total = correct + incorrect;
      var acc = total > 0 ? Math.round((correct / total) * 100) : 100;

      // consistency = 100 * (1 - coefficient of variation) over per-second raw wpm
      var sm = samples.filter(function (v) { return v >= 0; });
      var consistency = 100;
      if (sm.length > 1) {
        var mean = sm.reduce(function (a, b) { return a + b; }, 0) / sm.length;
        if (mean > 0) {
          var variance = sm.reduce(function (a, b) { return a + Math.pow(b - mean, 2); }, 0) / sm.length;
          var cv = Math.sqrt(variance) / mean;
          consistency = Math.max(0, Math.min(100, Math.round((1 - cv) * 100)));
        }
      }

      var finalWpm = isFinite(wpm) ? wpm : 0;
      var prevPb = pb;
      var isRecord = finalWpm > prevPb;
      if (isRecord) {
        pb = finalWpm;
        try { localStorage.setItem("wi_tt_pb", String(finalWpm)); } catch (e) {}
      }

      result = {
        wpm: finalWpm,
        raw: isFinite(raw) ? raw : 0,
        acc: acc,
        errors: incorrect,
        consistency: consistency,
        secs: Math.round(secs),
        correct: correct,
        chars: total,
        mode: mode,
        setting: mode === "time" ? timeOpt + "s" : wordOpt + " words",
        pb: Math.max(prevPb, finalWpm),
        isRecord: isRecord,
        errorsByKey: Object.assign({}, errKey)
      };
      updateReadout();
      renderResults();
    }

    /* ---------------------------- keyboard ---------------------------- */
    input.addEventListener("focus", function () { focused = true; updateFocusState(); });
    input.addEventListener("blur", function () { focused = false; updateFocusState(); });
    input.addEventListener("input", function () { input.value = ""; });

    input.addEventListener("keydown", function (e) {
      if (status === "finished") {
        if (e.key === "Enter") {
          e.preventDefault();
          resetTest(true);
        }
        return;
      }
      var ctrl = e.ctrlKey || e.metaKey;
      var k = e.key;
      var ai = active;

      if (k === "Enter") {
        e.preventDefault();
        if (ctrl) resetTest(true);
        else input.focus();
        return;
      }

      if (k === "Backspace") {
        e.preventDefault();
        var cur = typed[ai] || "";
        if (ctrl || e.altKey) {
          typed[ai] = "";
          refreshWord(ai);
          measureCaret();
        } else if (cur.length > 0) {
          typed[ai] = cur.slice(0, -1);
          refreshWord(ai);
          measureCaret();
        } else if (ai > 0) {
          active = ai - 1;
          repaintActive();
        }
        return;
      }

      if (k === " ") {
        e.preventDefault();
        var curw = typed[ai] || "";
        if (curw.length === 0 && status === "idle") return; // no leading space
        startIfIdle();
        correct += 1; // the separator counts as a correct keystroke
        var nextIndex = ai + 1;
        if (mode === "words" && nextIndex >= words.length) {
          finishTest();
          return;
        }
        // extend the buffer in time mode so fast typists never run out
        if (mode === "time" && nextIndex > words.length - 18) {
          var more = shuffleTake(WORD_POOL, 40, words);
          words = words.concat(more);
          typed = typed.concat(more.map(function () { return ""; }));
          for (var m = 0; m < more.length; m++) {
            var n = buildWordEl(words.length - more.length + m);
            wordEls.push(n);
            inner.appendChild(n);
          }
        }
        active = nextIndex;
        repaintActive();
        updateReadout();
        return;
      }

      if (k.length === 1 && !ctrl && !e.altKey) {
        e.preventDefault();
        startIfIdle();
        var target = words[ai] || "";
        var cur2 = typed[ai] || "";
        var pos = cur2.length;
        if (pos < target.length && k === target[pos]) {
          correct += 1;
        } else {
          incorrect += 1;
          if (pos < target.length) {
            var ek = target[pos];
            errKey[ek] = (errKey[ek] || 0) + 1;
          }
        }
        if (cur2.length < target.length + 8) {
          typed[ai] = cur2 + k;
          refreshWord(ai);
          measureCaret();
        }
        // auto-finish on completing the last word in words mode
        if (mode === "words" && ai === words.length - 1 && pos + 1 >= target.length && k === target[pos]) {
          window.setTimeout(function () { finishTest(); }, 0);
        }
        return;
      }
    });

    // Global Enter so "press enter to begin / restart" works even when the hidden
    // input isn't focused (which is exactly when the begin badge is showing).
    // Never steal Enter from another focused field on the page.
    function onWinKey(e) {
      if (e.key !== "Enter") return;
      var ae = document.activeElement;
      if (ae === input) return;
      var tag = ae && ae.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (ae && ae.isContentEditable)) return;
      if (!root.isConnected) return;
      e.preventDefault();
      if (status === "finished") resetTest(true);
      else input.focus();
    }
    window.addEventListener("keydown", onWinKey);

    /* ---- fill the viewport so the sticky footer stays below the fold ----------
     * The theme's footer reveal (snippets/wi-scroll-polish.liquid) pins the footer
     * at z-index:-1 and pads the body by the footer's height, expecting the page to
     * lap over it. On a page shorter than the viewport that padding is visible
     * immediately, exposing the footer's top slice — it reads as a rendering glitch.
     * Making the section at least fill the viewport restores the intended behaviour:
     * footer starts below the fold and reveals on scroll.
     */
    function applyFillHeight() {
      var host = root.closest(".shopify-section") || root;
      if (!fillViewport) { host.style.minHeight = ""; return; }
      host.style.minHeight = "";
      var top = host.getBoundingClientRect().top + (window.pageYOffset || 0);
      host.style.minHeight = "calc(100vh - " + Math.max(0, Math.round(top)) + "px)";
    }

    window.addEventListener("resize", function () {
      applyFillHeight();
      scrollToActive();
      measureCaret();
    });

    /* ---------------------------- results ---------------------------- */
    function renderResults() {
      if (!result) return;
      surface.style.display = "none";
      resultsHost.innerHTML = "";
      resultsHost.classList.add("is-on");

      var card = el("div", "wi-tt__res");

      var head = el("div", "wi-tt__res-head");
      head.appendChild(el("span", "wi-tt__res-lab", "// RESULTS"));
      head.appendChild(el("span", "wi-tt__res-lab wi-tt__res-lab--r",
        result.secs + "s · " + result.chars + " keys · " + String(result.setting).toUpperCase()));
      card.appendChild(head);

      var hero = el("div", "wi-tt__res-hero");
      var wpmCol = el("div", "wi-tt__res-col");
      var wpmTop = el("div", "wi-tt__res-wpmtop");
      wpmTop.appendChild(el("span", "wi-tt__res-lab", "wpm"));
      if (result.isRecord) {
        wpmTop.appendChild(el("span", "wi-tt__res-badge", "★ new record"));
      } else if (result.pb) {
        wpmTop.appendChild(el("span", "wi-tt__res-lab wi-tt__res-lab--faint", "pb " + result.pb));
      }
      wpmCol.appendChild(wpmTop);
      var wpmBig = el("span", "wi-tt__res-wpm", "0");
      wpmCol.appendChild(wpmBig);
      hero.appendChild(wpmCol);

      var statsWrap = el("div", "wi-tt__res-stats");
      [["raw", result.raw], ["accuracy", result.acc + "%"], ["errors", result.errors], ["consistency", result.consistency + "%"]]
        .forEach(function (s) {
          var c = el("div", "wi-tt__res-col");
          c.appendChild(el("span", "wi-tt__res-lab", s[0]));
          c.appendChild(el("span", "wi-tt__res-stat", String(s[1])));
          statsWrap.appendChild(c);
        });
      hero.appendChild(statsWrap);
      card.appendChild(hero);

      // consistency graph
      if (samples.length > 1) {
        var maxS = Math.max.apply(null, [1].concat(samples));
        var gWrap = el("div", "wi-tt__res-col");
        gWrap.appendChild(el("span", "wi-tt__res-lab", "wpm / second"));
        var graph = el("div", "wi-tt__graph");
        samples.forEach(function (sv) {
          var b = el("div", "wi-tt__bar-i");
          b.title = sv + " wpm";
          b.style.height = Math.max(3, (sv / maxS) * 100) + "%";
          b.style.opacity = String(0.45 + 0.55 * (sv / maxS));
          graph.appendChild(b);
        });
        gWrap.appendChild(graph);
        card.appendChild(gWrap);
      }

      // error heatmap
      var eb = result.errorsByKey || {};
      var keys = Object.keys(eb);
      var maxE = Math.max.apply(null, [1].concat(keys.map(function (k) { return Number(eb[k]); })));
      var totalErr = keys.reduce(function (a, k) { return a + Number(eb[k]); }, 0);
      var mapWrap = el("div", "wi-tt__res-col");
      mapWrap.appendChild(el("span", "wi-tt__res-lab", "// miss map" + (totalErr === 0 ? " · flawless run" : "")));
      var kb = el("div", "wi-tt__kb");
      KB_ROWS.forEach(function (row, ri) {
        var r = el("div", "wi-tt__kb-row");
        r.style.marginLeft = ri * 16 + "px";
        row.split("").forEach(function (ch) {
          var n = Number(eb[ch] || 0);
          var key = el("div", "wi-tt__key" + (n ? " is-miss" : ""), ch);
          key.title = n ? ch + " · " + n + " miss" : ch;
          if (n) key.style.background = "rgba(255,77,0," + (0.18 + 0.82 * (n / maxE)) + ")";
          r.appendChild(key);
        });
        kb.appendChild(r);
      });
      mapWrap.appendChild(kb);
      card.appendChild(mapWrap);

      // actions
      var actions = el("div", "wi-tt__res-actions");
      var again = el("span", "wi-tt__btn wi-tt__btn--solid", "↻ Run again");
      again.setAttribute("role", "button");
      again.tabIndex = -1;
      again.addEventListener("mousedown", function (e) {
        e.preventDefault();
        resetTest(true);
      });
      actions.appendChild(again);

      var save = el("span", "wi-tt__btn wi-tt__btn--ghost", "↓ Save card");
      save.setAttribute("role", "button");
      save.tabIndex = -1;
      save.addEventListener("mousedown", function (e) {
        e.preventDefault();
        save.textContent = "…";
        drawResultCard(result, samples, { dark: darkCol, accent: accent, cream: creamCol })
          .then(function (url) {
            if (url) {
              var a = document.createElement("a");
              a.href = url;
              a.download = "wired-in-type-test.png";
              document.body.appendChild(a);
              a.click();
              a.remove();
            }
          })
          .catch(function () {})
          .then(function () { save.textContent = "↓ Save card"; });
      });
      actions.appendChild(save);

      var link = document.createElement("a");
      link.className = "wi-tt__res-link";
      link.href = discordUrl;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.textContent = "Share your speeds on Discord →";
      actions.appendChild(link);

      card.appendChild(actions);
      resultsHost.appendChild(card);

      // Animated count-up on the hero WPM.
      // requestAnimationFrame is throttled to nothing in a hidden/background tab, so
      // the naive version (the Framer original included) can leave the hero reading 0
      // forever if the visitor finishes a run and immediately switches tabs. Land on
      // the exact value up front in that case, and always finish on the exact number
      // rather than the easing curve's 0.999 asymptote.
      var to = result.wpm;
      function landFinal() { wpmBig.textContent = String(to); }
      var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce || document.hidden || typeof requestAnimationFrame !== "function") {
        landFinal();
      } else {
        var dur = 850, start = Date.now();
        (function tick() {
          var t = Math.min(1, (Date.now() - start) / dur);
          if (t >= 1) { landFinal(); return; }
          wpmBig.textContent = String(Math.round(to * (1 - Math.pow(2, -10 * t))));
          requestAnimationFrame(tick);
        })();
        // safety net: if rAF never runs (tab hidden mid-animation), land anyway
        window.setTimeout(landFinal, dur + 120);
      }
    }

    // Debug surface — only attached when the host opts in with data-wi-tt-debug.
    // Used by the port harness to assert internal state; never present in production.
    if (root.hasAttribute("data-wi-tt-debug")) {
      root.__wiTT = {
        peek: function () {
          return { status: status, active: active, mode: mode, timeOpt: timeOpt,
                   wordOpt: wordOpt, correct: correct, incorrect: incorrect,
                   hasResult: !!result, samples: samples.length, pb: pb };
        },
        resetLog: []
      };
      var _reset = resetTest;
      resetTest = function (regen) {
        try { root.__wiTT.resetLog.push(new Error("resetTest").stack.split("\n").slice(1, 5).join(" | ")); } catch (e) {}
        return _reset.apply(null, arguments);
      };
    }

    /* ---------------------------- boot ---------------------------- */
    computePalette();
    applyPageTheme();
    syncToggle();
    renderBar();
    applyTypingFont();
    resetTest(true);
    applyFillHeight();

    // fonts land after first paint → re-measure so the caret isn't offset
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        applyFillHeight();
        scrollToActive();
        measureCaret();
      });
    }
  }

  function boot() {
    document.querySelectorAll("[data-wi-tt]").forEach(initTypeTest);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Shopify theme editor: re-init when the section is re-rendered
  document.addEventListener("shopify:section:load", boot);
})();
